import { useState } from 'react';
import { chartModules, getChartModule } from '../domain/catalog';
import type { ChartModuleId, ChartResult } from '../domain/types';
import { readSharePayload } from '../export/result';
import { consumeChunkRecoveryModule } from '../runtime/chunkRecovery';
import { AiPanel } from './AiPanel';
import { ChartForm } from './ChartForm';
import { ChartResultView } from './ChartResultView';
import { Icon } from './Icon';
import { ModulePicker } from './ModulePicker';

interface InitialChartState {
  selectedId: ChartModuleId;
  result: ChartResult | null;
  notice: string;
}

function getInitialChartState(): InitialChartState {
  const payload = readSharePayload();
  const sharedModule = payload ? getChartModule(payload.moduleId) : undefined;
  if (payload && sharedModule) {
    return {
      selectedId: sharedModule.id,
      result: { ...payload, moduleName: sharedModule.name, raw: { shared: true, note: '分享链接未包含原始 JSON。' } },
      notice: '已打开分享结果。链接不含原始数据和图片。'
    };
  }
  const recovery = consumeChunkRecoveryModule();
  const recoveredModule = recovery ? getChartModule(recovery.moduleId) : undefined;
  if (recovery && recoveredModule) {
    return { selectedId: recoveredModule.id, result: null, notice: recovery.notice };
  }
  return { selectedId: 'liuyao', result: null, notice: '' };
}

export function ChartStudio() {
  const [initialState] = useState(getInitialChartState);
  const [selectedId, setSelectedId] = useState<ChartModuleId>(initialState.selectedId);
  const [result, setResult] = useState<ChartResult | null>(initialState.result);
  const [sharedNotice] = useState(initialState.notice);
  const selected = getChartModule(selectedId) ?? chartModules[0];

  function selectModule(id: ChartModuleId) {
    setSelectedId(id);
    setResult(null);
  }

  return (
    <>
      <section className="hero-section">
        <div className="hero-copy">
          <span className="hero-kicker"><span />传统知识 · 本机排盘 · 隐私优先</span>
          <h1 tabIndex={-1}>易鉴</h1>
          <p className="hero-lead">把复杂术数，排成看得懂的结果。</p>
          <p className="hero-description">选择一种方法，填写时间和问题即可排盘。AI 解读完全可选；你也可以让当前浏览器加密记住 API 设置。</p>
          <div className="hero-stats" aria-label="产品能力概览">
            <div><strong>12</strong><span>门术数</span></div>
            <div><strong>27</strong><span>条知识</span></div>
            <div><strong>本机</strong><span>加密记住设置</span></div>
          </div>
        </div>
        <div className="hero-visual" aria-hidden="true">
          <span className="scholar-halo" />
          <img
            className="scholar-portrait"
            src="./hero-yinyang-scholar.webp"
            width="900"
            height="900"
            alt=""
            decoding="async"
            fetchPriority="high"
          />
          <div className="scholar-caption"><span>阴阳术士 · 观象仪</span><strong>持理以观象，知变而不妄断</strong></div>
        </div>
        <div className="hero-principle"><Icon name="shield" /><span><strong>只作研究与娱乐</strong>排盘不能替你做现实决定。</span></div>
      </section>

      <section className="quick-start" aria-labelledby="quick-start-title">
        <div className="quick-start-heading">
          <span className="eyebrow">第一次使用</span>
          <h2 id="quick-start-title">第一次使用，记住三步</h2>
          <p>先排盘，再看结果；需要时再打开 AI 解读。</p>
        </div>
        <ol className="quick-start-list">
          <li><strong>1</strong><div><b>选方法</b><span>不确定时，可先用六爻查看一般事项。</span></div></li>
          <li><strong>2</strong><div><b>看结果</b><span>填写时间和必要参数，点击“生成排盘”。</span></div></li>
          <li><strong>3</strong><div><b>按需解读</b><span>AI、导出和分享都可以稍后再用。</span></div></li>
        </ol>
      </section>

      <section className="studio-shell" aria-label="术数排盘工作台">
        <p className="visually-hidden" role="status">{result ? '排盘完成，结果如下。' : ''}</p>
        {sharedNotice && <p className="share-loaded-notice" role="status">{sharedNotice}</p>}
        <ModulePicker selectedId={selectedId} onSelect={selectModule} />
        <div className="studio-main">
          <ChartForm key={selectedId} module={selected} onResult={setResult} />
          {result ? <ChartResultView key={result.generatedAt} result={result} /> : (
            <div className="empty-result">
              <span className="empty-mark">卦</span>
              <h2>等待排盘</h2>
              <p>填好上面的内容，点击“生成排盘”即可。AI 解读可以不使用。</p>
            </div>
          )}
        </div>
        <AiPanel result={result} moduleId={selectedId} />
      </section>
    </>
  );
}
